import getRoomAvailabilityStore from '../store/roomAvailabilityStore';
import type { RoomAvailabilityState } from '../store/schema/RoomAvailabilityState';
import { getRoomPanelViewState } from '../selectors/getRoomPanelViewState';
import loc from 'owa-localize';
import { mutator, mutatorAction } from 'satcheljs';
import {
    roomListSectionRecentRoomLists,
    roomListSectionCities,
} from '../components/RoomFilter/CalendarRoomListPicker.locstring.json';
import {
    updateRoomListsInStore,
    updateRoomsInStore,
    updateUserRoomListConfig,
    updateSearchStateInStore,
    updatePreferredRoomListsInStore,
    updateRoomAvailabilityResults,
    initializeRoomListPicker,
    initRoomPanelViewState,
} from '../actions/calendarRoomActions';
import type CapabilityFilterViewState from '../store/schema/CapabilityFilterViewState';
import type RoomListViewState from '../store/schema/RoomListViewState';
import type { RoomPanelMetricInfo } from '../store/schema/RoomPanelMetricTypes';
import type { RoomList } from '../components/RoomSelector/RoomSelector.types';
import type { CompositePickerStore, ParentOption } from 'owa-calendar-composite-picker';
import { getStore } from '../store/store';

mutator(updateUserRoomListConfig, actionMessage => {
    let viewState = getRoomPanelViewState(actionMessage.viewStateId);
    if (viewState == null) {
        return;
    }

    let userCurrentRoomListName = actionMessage.userCurrentRoomListName;
    let userCurrentRoomListEmail = actionMessage.userCurrentRoomListEmail;

    if (viewState.roomListViewState.currentRoomList) {
        viewState.roomListViewState.currentRoomList.name = userCurrentRoomListName;
        viewState.roomListViewState.currentRoomList.email = userCurrentRoomListEmail;
    } else {
        viewState.roomListViewState.currentRoomList = {
            name: userCurrentRoomListName,
            email: userCurrentRoomListEmail,
        };
    }
});

mutator(updateRoomsInStore, actionMessage => {
    let viewState = getRoomPanelViewState(actionMessage.viewStateId);
    if (viewState == null) {
        return;
    }
    let rooms = actionMessage.rooms;
    viewState.roomListViewState.rooms = rooms;
});

mutator(updateRoomListsInStore, actionMessage => {
    let viewState = getRoomPanelViewState(actionMessage.viewStateId);
    if (viewState == null) {
        return;
    }
    let roomLists = actionMessage.roomLists;
    viewState.roomListViewState.roomLists = roomLists;
});

mutator(updatePreferredRoomListsInStore, actionMessage => {
    let viewState = getRoomPanelViewState(actionMessage.viewStateId);
    if (viewState == null) {
        return;
    }
    let prl = actionMessage.preferredRoomLists;
    viewState.roomListViewState.preferredRoomLists = prl;
});

mutator(updateSearchStateInStore, actionMessage => {
    let viewState = getRoomPanelViewState(actionMessage.viewStateId);
    if (viewState == null) {
        return;
    }
    let isSearching = actionMessage.isSearching;
    viewState.roomListViewState.isSearchingForRooms = isSearching;
});

mutator(updateRoomAvailabilityResults, actionMessage => {
    const {
        roomEmail,
        availability,
        roomRecurrenceAvailability,
        requestedCalendarTime,
        availabilityTimestamp,
    } = actionMessage;

    const roomAvailabilityStore = getRoomAvailabilityStore();
    if (roomAvailabilityStore == null) {
        return;
    }

    const roomAvailability: RoomAvailabilityState = {
        availability: availability,
        roomRecurrenceAvailability: roomRecurrenceAvailability,
        requestedCalendarTime: { ...requestedCalendarTime },
        availabilityTimestamp: availabilityTimestamp,
    };
    roomAvailabilityStore.roomAvailabilityStates.set(roomEmail, roomAvailability);
});

mutator(initializeRoomListPicker, ({ roomLists, citiesAndRoomLists, viewStateId }) => {
    const { roomListPickerViewState } = getRoomPanelViewState(viewStateId);
    let currentIndex = roomLists.length;

    const options = [
        {
            account: loc(roomListSectionRecentRoomLists),
            isPrimaryAccount: true,
            leafOptions: (roomLists || []).map((roomList, index) => ({
                id: roomList.email,
                index: index,
                displayName: roomList.name,
                account: loc(roomListSectionRecentRoomLists),
                isShown: true,
            })),
            parentOptions: [],
        },
        {
            account: loc(roomListSectionCities),
            isPrimaryAccount: false,
            leafOptions: [],
            parentOptions: Object.keys(citiesAndRoomLists)
                .sort()
                .map(city => ({
                    id: city,
                    index: currentIndex++,
                    displayName: city,
                    account: loc(roomListSectionCities),
                    isShown: true,
                    isExpanded: false,
                    isFavorite: false,
                    children: citiesAndRoomLists[city].map(roomList => ({
                        id: roomList.email,
                        index: currentIndex++,
                        displayName: roomList.name,
                        account: loc(roomListSectionCities),
                        isShown: false,
                    })),
                })),
        },
    ];
    roomListPickerViewState.options = options;
    roomListPickerViewState.filteredOptions = options;
});

mutator(initRoomPanelViewState, actionMessage => {
    const store = getStore();
    const viewStateId = actionMessage.viewStateId;
    if (!store.viewStates.has(viewStateId)) {
        const defaultRoomListState: RoomListViewState = actionMessage.roomListViewState;

        const defaultRoomFilterState: CapabilityFilterViewState = {
            expandCapacity: false,
            expandFeature: false,
            expandFloor: false,
            selectedCapacity: null,
            selectedFloor: null,
            selectedFeatures: [],
            selectedFilters: null,
            selectedTags: [],
            shouldGetSpaces: false,
        };

        const defaultRoomPanelMetricsState: Partial<RoomPanelMetricInfo> = {
            totalNumSelectedCities: 0,
            totalNumSelectedRoomLists: 0,
            selectedFromSuggestion: false,
            selectedFromSearch: false,
            isFilterOpened: false,
            isFilterApplied: false,
            isCapacityFilterApplied: false,
            isFeatureFilterApplied: false,
            isFloorFilterApplied: false,
            isCapacityEnabled: false,
            isAudioEnabled: false,
            isDisplayEnabled: false,
            isVideoEnabled: false,
            isAccessibleEnabled: false,
            isFloorEnabled: false,
            queryStringChanged: false,
            isWorkspaceBookingEnabled: false,
        };

        const defaultRoomListPickerViewState: CompositePickerStore = {
            options: [],
            filteredOptions: [],
            selectedOption: null,
            textFieldValue: '',
            navIndex: -1,
        };

        store.viewStates.set(viewStateId, {
            roomFilterState: defaultRoomFilterState,
            roomPanelMetricsState: defaultRoomPanelMetricsState,
            roomListViewState: defaultRoomListState,
            opxRoomFinderData: null,
            roomListPickerViewState: defaultRoomListPickerViewState,
        });
    }
});

export const addChildrenInRoomListPicker = mutatorAction(
    'addChildrenInRoomListPicker',
    (roomLists: RoomList[], parentOption: ParentOption) => {
        parentOption.children = roomLists.map(roomList => ({
            id: roomList.email,
            index: 0,
            displayName: roomList.name,
            account: loc(roomListSectionCities),
            isShown: true,
        }));
    }
);

export const updateShowPlaceType = mutatorAction(
    'updateShowPlaceType',
    (showPlaceType: boolean, viewStateId: string | null) => {
        const { roomListViewState } = getRoomPanelViewState(viewStateId);
        roomListViewState.showPlaceType = showPlaceType;
    }
);

export type { default as RoomType } from 'owa-service/lib/contract/RoomType';
export type { default as GetRoomListsResponse } from 'owa-service/lib/contract/GetRoomListsResponse';
